/**
 * 집 구조 transform 출력 → 사람이 읽는 방 목록.
 *
 * 구조 기록 문서의 마지막 transform 은 `{type, index, metadata}` 배열을 낸다. 완료
 * 화면과 그 뒤의 모든 단계는 "현관 / 거실 / 1번 방(화장실·베란다)" 처럼 이름 붙은
 * 목록을 쓴다. 그 사이 변환이 지금까지 두 곳에 따로 있었다 — 프로덕션의
 * `transformRoomsToFlowRooms` 와 프리뷰의 같은 이름 함수.
 *
 * 규칙이 갈리면 완료 화면에 뜬 방 이름과 이후 단계의 방 이름이 달라진다. 한 벌만 둔다.
 */

const TYPE_TO_LABEL: Record<string, string> = {
  ENTRANCE: "현관",
  LIVING_ROOM: "거실",
  KITCHEN: "주방",
  ROOM: "방",
  BATH_ROOM: "화장실",
  BALCONY: "베란다",
  RESIDENCE_STORAGE: "세대창고",
  OTHER_SPACE: "기타",
};

/**
 * 사람이 읽는 라벨 → 룸 타입.
 *
 * 수정 화면에서 새로 추가된 방은 라벨만 갖는다("거실", "2번 방", 직접 입력한 이름).
 * 방 선택 화면은 타입으로 섹션과 이모지를 정하므로 되돌려 읽어야 한다. 번호 접두어는
 * 타입과 무관하니 떼고 본다.
 */
export function labelToRoomType(label: string): string {
  const base = label.replace(/^\d+번\s*/, "").trim();
  const hit = Object.entries(TYPE_TO_LABEL).find(([, v]) => v === base);
  return hit ? hit[0] : "OTHER_SPACE";
}

export interface TransformRoom {
  type: string;
  index: number;
  metadata: Record<string, unknown>;
}

export interface FlowRoom {
  id: string;
  label: string;
  tags: string[];
  type: string;
}

export function transformRoomsToFlowRooms(
  rooms: readonly TransformRoom[],
): FlowRoom[] {
  const typeMaxIndex: Record<string, number> = {};
  for (const room of rooms) {
    typeMaxIndex[room.type] = Math.max(typeMaxIndex[room.type] ?? 0, room.index);
  }

  return rooms.map((room, idx) => {
    const baseLabel = TYPE_TO_LABEL[room.type] ?? room.type;
    let label: string;
    if (room.type === "OTHER_SPACE") {
      label = typeof room.metadata.name === "string" ? room.metadata.name : "기타";
    } else if ((typeMaxIndex[room.type] ?? 0) > 1) {
      label = `${room.index}번 ${baseLabel}`;
    } else {
      label = baseLabel;
    }

    const tags: string[] = [];
    if (room.metadata.TOILET) tags.push("화장실");
    if (room.metadata.BALCONY) tags.push("베란다");

    return { id: `t_${room.type}_${room.index}_${idx}`, label, tags, type: room.type };
  });
}

/** transform 스텝의 출력에서 방 배열을 꺼낸다. 출력 모양이 두 가지라 둘 다 받는다. */
export function readTransformRooms(raw: unknown): TransformRoom[] {
  const container = raw as { rooms?: unknown[] } | undefined;
  const list = Array.isArray(container?.rooms)
    ? container.rooms
    : Array.isArray(raw)
      ? (raw as unknown[])
      : [];

  return list.map((item, index) => {
    const room = (item ?? {}) as Record<string, unknown>;
    return {
      type: typeof room.type === "string" ? room.type : "OTHER_SPACE",
      index: typeof room.index === "number" ? room.index : index + 1,
      metadata:
        room.metadata && typeof room.metadata === "object"
          ? (room.metadata as Record<string, unknown>)
          : {},
    };
  });
}

/**
 * 완료 화면이 그릴 방 목록.
 *
 * 이미 이름이 붙은 목록(수정 화면을 거쳐 돌아온 경우)이면 그대로 쓴다. 두 번 이름을
 * 붙이면 "거실" 이 "2번 거실" 이 된다.
 */
export function houseStructureRooms(raw: unknown): FlowRoom[] {
  const list = Array.isArray((raw as { rooms?: unknown[] })?.rooms)
    ? ((raw as { rooms: unknown[] }).rooms)
    : Array.isArray(raw)
      ? (raw as unknown[])
      : [];

  const labelled =
    list.length > 0 &&
    list.every((item) => typeof (item as { label?: unknown })?.label === "string");

  if (labelled) {
    return (list as Array<{ id?: string; label: string; type?: string; tags?: string[] }>).map(
      (room, i) => ({
        id: room.id ?? `room_${i}`,
        label: room.label,
        tags: room.tags ?? [],
        type: room.type ?? labelToRoomType(room.label),
      }),
    );
  }

  return transformRoomsToFlowRooms(readTransformRooms(raw));
}
